import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    Lead,
    LoanLead,
    LeadLoanType,
    LeadContactDetailsPreferredChannel,
    Channel,
    ReferralSourceType,
    RegistrationNumberType
} from '../../services/schemas/loanServiceSchemas';
import {
    LEGAL_ENTITY_TYPES,
    getLegalEntityType,
    normalizeRegistrationNumber,
    registrationLabelFor,
    registrationPlaceholderFor,
    validateRegistrationNumber
} from '../../utils/validation/legalEntityType';
import { validateSouthAfricanIdNumber } from '../../utils/validation/taxPinAndSouthAfricanId';
import { INDIVIDUAL_ID_TYPES, IndividualIdType, usesCustomerIdentity, validateIndividualNumber } from '../../utils/validation/individualIdentification';
// The same banks the loan application's Bank Details step offers.
import { normalizeBankName, southAfricanBanks } from '../loan-application/steps/BankAccountStep';

/*
 * The lead form shared by Create Lead and Edit Lead.
 *
 * A lead is captured before most of its details are known, so no field is required: whatever
 * is typed is checked for format, and the rest can be filled in later by editing the lead. The
 * one rule is that there must be something to follow up - a name, a company, a phone number or
 * an email - which the backend enforces too.
 *
 * Amounts and counts are held as text so an untouched field stays empty instead of becoming 0.
 */

const blank = (value?: string) => !value?.trim();

const isNumber = (value?: string) => blank(value) || !Number.isNaN(Number(value));

const leadFormSchema = z.object({
    loanType: z.enum([LeadLoanType.BUSINESS, LeadLoanType.PERSONAL]),
    // Set when an existing customer was picked: its identity comes from the customer record and
    // may predate the format rules, and staff cannot edit it here.
    existingCustomer: z.boolean().optional(),
    registeredName: z.string().optional(),
    // Same rules as the Customer Info step (utils/validation/legalEntityType): the legal form
    // decides what a valid registration number is. Carried onto the customer on conversion.
    legalEntityType: z.string().optional(),
    registrationNumber: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    idNumber: z.string().optional(),
    // How a personal lead is identified: RSA ID, passport, or Customer ID for a historical client (no document)
    idType: z.enum(['SA_ID', 'PASSPORT', 'CUSTOMER_ID']).optional(),
    loanAmount: z.string().optional(),
    primaryPhone: z.string().optional(),
    email: z.string().optional(),
    preferredChannel: z.enum([
        LeadContactDetailsPreferredChannel.EMAIL,
        LeadContactDetailsPreferredChannel.WHATSAPP,
        LeadContactDetailsPreferredChannel.SMS
    ]),
    // How the lead reached us. Set when the lead is created and not changed afterwards.
    channel: z.enum([
        Channel.WEBSITE,
        Channel.WHATSAPP,
        Channel.FACEBOOK,
        Channel.PHONE,
        Channel.CAMPAIGN,
        Channel.LMS
    ]),
    // Who brought the lead in. Left empty, the LMS records Universal.
    agentId: z.string().max(60).optional(),
    branchId: z.string().max(60).optional(),
    campaignId: z.string().max(60).optional(),
    // Who sent it. Kept separate from the channel: a lead can arrive over WhatsApp from a
    // broker, and collapsing the two would lose one of them.
    referralSourceType: z.string().optional(),
    referralSource: z.string().optional(),
    referralNotes: z.string().optional(),
    // More details - the fields the website form captures, so a lead taken by phone can be
    // brought up to the same level.
    tradingName: z.string().optional(),
    industrySector: z.string().optional(),
    establishmentDate: z.string().optional(),
    employeeCount: z.string().optional(),
    physicalAddress: z.string().optional(),
    representativeOccupation: z.string().optional(),
    designation: z.string().optional(),
    ownershipPercentage: z.string().optional(),
    turnover: z.string().optional(),
    bankName: z.string().optional(),
    incomeType: z.string().optional(),
    vatNumber: z.string().optional(),
    taxNumber: z.string().optional(),
    // Carries the "nothing to follow up" error; not a field of its own.
    followUp: z.any().optional()
}).superRefine((data, ctx) => {
    const issue = (path: string, message: string) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });
    const isBusiness = data.loanType === LeadLoanType.BUSINESS;

    const nothingToFollowUp = blank(data.firstName) && blank(data.lastName)
        && (!isBusiness || blank(data.registeredName))
        && blank(data.primaryPhone) && blank(data.email);
    if (nothingToFollowUp) {
        issue('followUp', 'Capture at least a name, company, phone number or email so the lead can be followed up.');
    }

    if (!blank(data.loanAmount)) {
        const amount = Number(data.loanAmount);
        if (Number.isNaN(amount)) {
            issue('loanAmount', 'Enter the amount as a number');
        } else if (amount < 1000) {
            issue('loanAmount', 'Minimum loan amount is R1,000');
        }
    }

    if (!blank(data.email) && !z.string().email().safeParse(data.email!.trim()).success) {
        issue('email', 'Enter a valid email address');
    }

    if (!blank(data.idNumber) && !data.existingCustomer) {
        // A personal lead's number follows its identification type; a business contact's is an RSA ID
        const result = !isBusiness ? validateIndividualNumber(data.idNumber!.trim(), data.idType as IndividualIdType)
            : validateSouthAfricanIdNumber(data.idNumber);
        if (result !== true) {
            issue('idNumber', result);
        }
    }

    if (isBusiness && !blank(data.registrationNumber) && !data.existingCustomer) {
        const result = validateRegistrationNumber(data.registrationNumber, data.legalEntityType);
        if (result !== true) {
            issue('registrationNumber', result);
        }
    }

    if (!isNumber(data.employeeCount)) issue('employeeCount', 'Enter a number');
    if (!isNumber(data.turnover)) issue('turnover', 'Enter the amount as a number');
    if (!isNumber(data.ownershipPercentage)) {
        issue('ownershipPercentage', 'Enter a number');
    } else if (!blank(data.ownershipPercentage)) {
        const percentage = Number(data.ownershipPercentage);
        if (percentage < 0 || percentage > 100) issue('ownershipPercentage', 'Enter a percentage from 0 to 100');
    }
});

export type LeadFormValues = z.infer<typeof leadFormSchema>;

export const emptyLeadFormValues = (loanType: LeadLoanType = LeadLoanType.BUSINESS): LeadFormValues => ({
    loanType: loanType as LeadFormValues['loanType'],
    existingCustomer: false,
    registeredName: '',
    legalEntityType: '',
    registrationNumber: '',
    firstName: '',
    lastName: '',
    idNumber: '',
    idType: 'SA_ID',
    loanAmount: '',
    primaryPhone: '',
    email: '',
    preferredChannel: LeadContactDetailsPreferredChannel.EMAIL,
    channel: Channel.LMS,
    agentId: '',
    branchId: '',
    campaignId: '',
    referralSourceType: '',
    referralSource: '',
    referralNotes: '',
    tradingName: '',
    industrySector: '',
    establishmentDate: '',
    employeeCount: '',
    physicalAddress: '',
    representativeOccupation: '',
    designation: '',
    ownershipPercentage: '',
    turnover: '',
    bankName: '',
    incomeType: '',
    vatNumber: '',
    taxNumber: ''
});

const asText = (value?: number | string | null) => (value === undefined || value === null ? '' : String(value));

/** The form's values for an existing lead. */
export const leadToFormValues = (lead: LoanLead): LeadFormValues => {
    const preferred = lead.preferredChannel as string | undefined;
    return {
        ...emptyLeadFormValues(lead.loanType === LeadLoanType.PERSONAL ? LeadLoanType.PERSONAL : LeadLoanType.BUSINESS),
        registeredName: lead.registeredName ?? '',
        legalEntityType: lead.legalEntityType ?? '',
        registrationNumber: lead.registrationNumber ?? '',
        firstName: lead.firstName ?? '',
        lastName: lead.lastName ?? '',
        idNumber: lead.idNumber ?? '',
        idType: (lead.idType as IndividualIdType | undefined) ?? 'SA_ID',
        loanAmount: lead.loanAmount ? String(lead.loanAmount) : '',
        primaryPhone: lead.primaryPhone ?? '',
        email: lead.email ?? '',
        // MOBILE is stored by some older leads; the form offers the three channels staff choose from.
        preferredChannel: preferred === LeadContactDetailsPreferredChannel.WHATSAPP || preferred === LeadContactDetailsPreferredChannel.SMS
            ? preferred
            : LeadContactDetailsPreferredChannel.EMAIL,
        referralSourceType: lead.referralSourceType ?? '',
        referralSource: lead.referralSource ?? '',
        referralNotes: lead.referralNotes ?? '',
        tradingName: lead.tradingName ?? '',
        industrySector: lead.industrySector ?? '',
        establishmentDate: lead.establishmentDate ?? '',
        employeeCount: asText(lead.employeeCount),
        physicalAddress: lead.physicalAddress ?? '',
        representativeOccupation: lead.representativeOccupation ?? '',
        designation: lead.designation ?? '',
        ownershipPercentage: asText(lead.ownershipPercentage),
        turnover: asText(lead.turnover),
        bankName: normalizeBankName(lead.bankName ?? ''),
        incomeType: lead.incomeType ?? '',
        vatNumber: lead.vatNumber ?? '',
        taxNumber: lead.taxNumber ?? ''
    };
};

const text = (value?: string) => value?.trim() || undefined;
const number = (value?: string) => (blank(value) ? undefined : Number(value));

/**
 * The request body for the form's values. An update replaces every detail of the lead, so this
 * always sends the full set; `original` supplies what the form does not show.
 *
 * A personal lead sends no company details, so switching a lead from business to personal
 * clears them rather than leaving a company behind on an individual.
 */
export const toLeadPayload = (values: LeadFormValues, original?: LoanLead): Lead => {
    const isBusiness = values.loanType === LeadLoanType.BUSINESS;
    const registrationNumber = normalizeRegistrationNumber(values.registrationNumber) || undefined;
    // How to read the registration number is not asked here. Keep it while the number and the
    // legal form it was recorded against are unchanged; otherwise it no longer describes them.
    const keepRegistrationNumberType = isBusiness && !!original
        && (original.registrationNumber ?? undefined) === registrationNumber
        && (original.legalEntityType ?? '') === (values.legalEntityType ?? '');

    return {
        loanType: values.loanType,
        firstName: text(values.firstName),
        lastName: text(values.lastName),
        idNumber: !isBusiness && usesCustomerIdentity(values.idType) ? undefined : text(values.idNumber),
        idType: isBusiness ? undefined : values.idType,
        loanAmount: number(values.loanAmount),
        registeredName: isBusiness ? text(values.registeredName) : undefined,
        legalEntityType: isBusiness ? text(values.legalEntityType) : undefined,
        registrationNumber: isBusiness ? registrationNumber : undefined,
        registrationNumberType: keepRegistrationNumberType
            ? original!.registrationNumberType as RegistrationNumberType | undefined
            : undefined,
        tradingName: isBusiness ? text(values.tradingName) : undefined,
        industrySector: isBusiness ? text(values.industrySector) : undefined,
        establishmentDate: isBusiness ? text(values.establishmentDate) : undefined,
        employeeCount: isBusiness ? number(values.employeeCount) : undefined,
        designation: isBusiness ? text(values.designation) : undefined,
        ownershipPercentage: isBusiness ? number(values.ownershipPercentage) : undefined,
        turnover: isBusiness ? number(values.turnover) : undefined,
        vatNumber: isBusiness ? text(values.vatNumber) : undefined,
        incomeType: isBusiness ? text(values.incomeType) : undefined,
        representativeOccupation: text(values.representativeOccupation),
        physicalAddress: text(values.physicalAddress),
        bankName: text(values.bankName),
        taxNumber: text(values.taxNumber),
        agentId: text(values.agentId),
        branchId: text(values.branchId),
        campaignId: text(values.campaignId),
        // Only send referral details when a source was actually chosen, so an untouched form
        // does not record an empty referral against the lead.
        ...(values.referralSourceType
            ? {
                referralSourceType: values.referralSourceType as ReferralSourceType,
                referralSource: text(values.referralSource),
                referralNotes: text(values.referralNotes)
            }
            // The website writes the customer's enquiry here on a lead with no referrer.
            : { referralNotes: original?.referralNotes }),
        contactDetails: {
            primaryPhone: text(values.primaryPhone),
            email: text(values.email),
            preferredChannel: values.preferredChannel
        }
    };
};

interface LeadFormProps {
    defaultValues: LeadFormValues;
    mode: 'create' | 'edit';
    /** An existing customer was picked: the loan type and their identity come from that record. */
    lockIdentity?: boolean;
    isPending: boolean;
    errorMessage?: string;
    onSubmit: (values: LeadFormValues) => void;
    /** Without one, Cancel dismisses the surrounding Bootstrap modal. */
    onCancel?: () => void;
}

const LeadForm: React.FC<LeadFormProps> = ({
    defaultValues,
    mode,
    lockIdentity = false,
    isPending,
    errorMessage,
    onSubmit,
    onCancel
}) => {
    const { register, handleSubmit, watch, control, setValue, formState: { errors } } = useForm<LeadFormValues>({
        resolver: zodResolver(leadFormSchema),
        defaultValues
    });
    // Editing is where missing details get filled in, so the extra fields start open there.
    const [showMoreDetails, setShowMoreDetails] = useState(mode === 'edit');

    const loanType = watch('loanType');
    // A stored bank that is not on the list stays selectable rather than being silently dropped.
    const bankName = watch('bankName');
    const unlistedBank = !!bankName && !southAfricanBanks.some(bank => bank.name === bankName);
    const isBusiness = loanType === LeadLoanType.BUSINESS;
    // Drives the referrer fields: they only make sense once a referral source is chosen.
    const referralSourceType = watch('referralSourceType');
    // Drives the registration number's label, placeholder and format.
    const legalEntityType = watch('legalEntityType');
    const selectedLegalEntityType = getLegalEntityType(legalEntityType);

    const inputClass = (field: keyof LeadFormValues, readOnly = false) =>
        `form-control ${errors[field] ? 'is-invalid' : ''} ${readOnly ? 'bg-light' : ''}`;

    const fieldError = (field: keyof LeadFormValues) =>
        errors[field]?.message ? <div className="invalid-feedback">{String(errors[field]?.message)}</div> : null;

    const loanTypeOption = (value: LeadFormValues['loanType'], icon: string, label: string, hint: string) => {
        const selected = loanType === value;
        return (
            <div className="col-6">
                <button
                    type="button"
                    className={`btn w-100 h-100 text-start p-3 border-2 text-wrap ${selected ? 'btn-outline-primary active' : 'btn-outline-secondary'}`}
                    style={{ whiteSpace: 'normal' }}
                    onClick={() => setValue('loanType', value, { shouldValidate: false })}
                    disabled={lockIdentity}
                    aria-pressed={selected}
                >
                    <div className="d-flex align-items-center">
                        <i className={`fa ${icon} fa-lg me-3`}></i>
                        <div>
                            <div className="fw-bold">{label}</div>
                            <small className={selected ? '' : 'text-muted'}>{hint}</small>
                        </div>
                    </div>
                </button>
            </div>
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex-shrink-0">
            {/* Loan type - decides whether the lead converts into a company or an individual */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body p-4">
                    <label className="form-label fw-semibold mb-2">Loan Type</label>
                    <div className="row g-3">
                        {loanTypeOption(LeadLoanType.BUSINESS, 'fa-building', 'Business loan', 'For a company, practice, trust or sole proprietor')}
                        {loanTypeOption(LeadLoanType.PERSONAL, 'fa-user', 'Personal loan', 'For an individual')}
                    </div>
                    {lockIdentity && (
                        <div className="form-text">Set by the customer you picked.</div>
                    )}
                </div>
            </div>

            {errors.followUp?.message && (
                <div className="alert alert-warning d-flex align-items-center">
                    <i className="fa fa-exclamation-triangle me-2"></i>
                    {String(errors.followUp.message)}
                </div>
            )}

            {/* Lead Information */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-3">
                        <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                            <i className="fa fa-info-circle text-primary" style={{ display: 'block' }}></i>
                        </div>
                        <div>
                            <h6 className="mb-0 fw-bold">Lead Information</h6>
                            <small className="text-muted">
                                Capture what you have now; anything missing can be added later by editing the lead.
                            </small>
                        </div>
                    </div>

                    <div className="row g-3">
                        {isBusiness && (
                            <>
                                <div className="col-md-12">
                                    <label className="form-label fw-semibold mb-2">Company Name</label>
                                    <input
                                        type="text"
                                        className={inputClass('registeredName', lockIdentity)}
                                        placeholder="Registered company or practice name"
                                        {...register('registeredName')}
                                        readOnly={lockIdentity}
                                    />
                                    {fieldError('registeredName')}
                                </div>

                                {/*
                                    Legal entity type comes BEFORE the registration number, as on the
                                    Customer Info step: it decides what a valid number is. A practice
                                    number or a trust's IT number is not a CIPC number.
                                */}
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold mb-2">Legal Entity Type</label>
                                    <select
                                        className={`form-select ${errors.legalEntityType ? 'is-invalid' : ''}`}
                                        {...register('legalEntityType')}
                                    >
                                        <option value="">Select entity type...</option>
                                        {LEGAL_ENTITY_TYPES.map((entityType) => (
                                            <option key={entityType.code} value={entityType.code}>
                                                {entityType.label}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedLegalEntityType?.hint && (
                                        <div className="form-text">{selectedLegalEntityType.hint}</div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold mb-2">
                                        {registrationLabelFor(legalEntityType)}
                                    </label>
                                    <input
                                        type="text"
                                        className={inputClass('registrationNumber', lockIdentity)}
                                        placeholder={registrationPlaceholderFor(legalEntityType)}
                                        {...register('registrationNumber', {
                                            // "2021 / 359709 / 07" and "2021/359709/07" were stored as two companies.
                                            setValueAs: (value: string) => normalizeRegistrationNumber(value)
                                        })}
                                        readOnly={lockIdentity}
                                    />
                                    {errors.registrationNumber ? (
                                        fieldError('registrationNumber')
                                    ) : (
                                        selectedLegalEntityType?.usesOwnerIdentity && (
                                            <div className="form-text">
                                                This entity has no registration of its own, so it is identified by the person behind it.
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        )}

                        <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2">
                                {isBusiness ? 'Contact First Name' : 'First Name'}
                            </label>
                            <input
                                type="text"
                                className={inputClass('firstName', lockIdentity && !isBusiness)}
                                placeholder="Enter first name"
                                {...register('firstName')}
                                readOnly={lockIdentity && !isBusiness}
                            />
                            {fieldError('firstName')}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2">
                                {isBusiness ? 'Contact Last Name' : 'Last Name'}
                            </label>
                            <input
                                type="text"
                                className={inputClass('lastName', lockIdentity && !isBusiness)}
                                placeholder="Enter last name"
                                {...register('lastName')}
                                readOnly={lockIdentity && !isBusiness}
                            />
                            {fieldError('lastName')}
                        </div>

                        {!isBusiness && (
                            <div className="col-md-6">
                                <label className="form-label fw-semibold mb-2">Identification Type</label>
                                <select className="form-select" {...register('idType')} disabled={lockIdentity}>
                                    {INDIVIDUAL_ID_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                                </select>
                            </div>
                        )}
                        {!isBusiness && !usesCustomerIdentity(watch('idType')) && (
                            <div className="col-md-6">
                                <label className="form-label fw-semibold mb-2">{watch('idType') === 'PASSPORT' ? 'Passport Number' : 'ID Number'}</label>
                                <input
                                    type="text"
                                    inputMode={watch('idType') === 'PASSPORT' ? 'text' : 'numeric'}
                                    className={inputClass('idNumber', lockIdentity)}
                                    placeholder={watch('idType') === 'PASSPORT' ? 'Passport number' : '13-digit South African ID number'}
                                    {...register('idNumber')}
                                    readOnly={lockIdentity}
                                />
                                {fieldError('idNumber')}
                            </div>
                        )}
                        {!isBusiness && usesCustomerIdentity(watch('idType')) && (
                            <div className="col-md-6">
                                <label className="form-label fw-semibold mb-2">Customer ID</label>
                                <p className="small text-muted mb-0">Historical client with no ID or passport on file. The customer number is used once the lead becomes a customer, and they cannot be checked at the credit bureau.</p>
                            </div>
                        )}

                        <div className={isBusiness ? 'col-md-12' : 'col-md-6'}>
                            <label className="form-label fw-semibold mb-2">Loan Amount (ZAR)</label>
                            <input
                                type="number"
                                min={0}
                                step="0.01"
                                className={inputClass('loanAmount')}
                                placeholder="0.00"
                                {...register('loanAmount')}
                            />
                            {fieldError('loanAmount')}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2">Phone Number</label>
                            <input
                                type="tel"
                                className={inputClass('primaryPhone')}
                                placeholder="10-digit phone number"
                                {...register('primaryPhone')}
                            />
                            {fieldError('primaryPhone')}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2">Email Address</label>
                            <input
                                type="email"
                                className={inputClass('email')}
                                placeholder="email@example.com"
                                {...register('email')}
                            />
                            {fieldError('email')}
                        </div>

                        <div className="col-md-12">
                            <label className="form-label fw-semibold mb-2">Preferred Communication Channel</label>
                            <Controller
                                name="preferredChannel"
                                control={control}
                                render={({ field }) => (
                                    <select className="form-select" {...field}>
                                        <option value={LeadContactDetailsPreferredChannel.EMAIL}>Email</option>
                                        <option value={LeadContactDetailsPreferredChannel.WHATSAPP}>WhatsApp</option>
                                        <option value={LeadContactDetailsPreferredChannel.SMS}>SMS</option>
                                    </select>
                                )}
                            />
                        </div>

                        <div className="col-md-12">
                            <hr className="my-3" />
                            <h6 className="fw-bold mb-3">
                                <i className="fa fa-share-alt text-primary me-2"></i>
                                Lead Source
                            </h6>
                        </div>

                        {mode === 'create' && (
                            <div className="col-md-6">
                                <label className="form-label fw-semibold mb-2">How did this lead reach us?</label>
                                <Controller
                                    name="channel"
                                    control={control}
                                    render={({ field }) => (
                                        <select className="form-select" {...field}>
                                            <option value={Channel.LMS}>Captured in the office</option>
                                            <option value={Channel.WEBSITE}>Website</option>
                                            <option value={Channel.WHATSAPP}>WhatsApp</option>
                                            <option value={Channel.FACEBOOK}>Facebook</option>
                                            <option value={Channel.PHONE}>Phone</option>
                                            <option value={Channel.CAMPAIGN}>Campaign</option>
                                        </select>
                                    )}
                                />
                            </div>
                        )}

                        {mode === 'create' && (
                            <>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold mb-2">Agent ID</label>
                                    <input type="text" className="form-control" maxLength={60} placeholder="Universal" {...register('agentId')} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold mb-2">Branch ID</label>
                                    <input type="text" className="form-control" maxLength={60} placeholder="Universal" {...register('branchId')} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold mb-2">Campaign ID</label>
                                    <input type="text" className="form-control" maxLength={60} placeholder="Universal" {...register('campaignId')} />
                                </div>
                            </>
                        )}

                        <div className="col-md-6">
                            <label className="form-label fw-semibold mb-2">Referred by</label>
                            <select className="form-select" {...register('referralSourceType')}>
                                <option value="">Not a referral</option>
                                <option value={ReferralSourceType.BROKER}>Broker</option>
                                <option value={ReferralSourceType.EXISTING_CLIENT}>Existing client</option>
                                <option value={ReferralSourceType.STAFF}>Staff member</option>
                                <option value={ReferralSourceType.WALK_IN}>Walk-in</option>
                                <option value={ReferralSourceType.EVENT}>Event</option>
                                <option value={ReferralSourceType.OTHER}>Other</option>
                            </select>
                            <div className="form-text">Who sent the lead, separate from how it arrived.</div>
                        </div>

                        {referralSourceType && (
                            <>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold mb-2">Name of the referrer</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g. Sipho Nkosi or Mzansi Brokers"
                                        {...register('referralSource')}
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label fw-semibold mb-2">Referral notes</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Optional context, e.g. how they know us"
                                        {...register('referralNotes')}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* More details - what the website form captures, so it can be filled in for any lead */}
            <div className="card border-0 shadow-sm mb-3">
                <div className="card-body p-4">
                    <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none fw-bold d-flex align-items-center"
                        onClick={() => setShowMoreDetails(show => !show)}
                        aria-expanded={showMoreDetails}
                    >
                        <i className={`fa ${showMoreDetails ? 'fa-chevron-down' : 'fa-chevron-right'} me-2`}></i>
                        More details
                    </button>

                    {showMoreDetails && (
                        <div className="row g-3 mt-1">
                            {isBusiness && (
                                <>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Trading Name</label>
                                        <input type="text" className="form-control" placeholder="If different from the registered name" {...register('tradingName')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Industry Sector</label>
                                        <input type="text" className="form-control" placeholder="e.g. Retail" {...register('industrySector')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Establishment Date</label>
                                        <input type="text" className="form-control" placeholder="Year or date trading started" {...register('establishmentDate')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Number of Employees</label>
                                        <input type="number" min={0} className={inputClass('employeeCount')} {...register('employeeCount')} />
                                        {fieldError('employeeCount')}
                                    </div>
                                </>
                            )}

                            <div className="col-md-12">
                                <label className="form-label fw-semibold mb-2">{isBusiness ? 'Physical Address' : 'Home Address'}</label>
                                <input type="text" className="form-control" {...register('physicalAddress')} />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-semibold mb-2">{isBusiness ? 'Contact Occupation / Role' : 'Occupation'}</label>
                                <input type="text" className="form-control" {...register('representativeOccupation')} />
                            </div>

                            {isBusiness && (
                                <>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Designation</label>
                                        <input type="text" className="form-control" placeholder="e.g. Director" {...register('designation')} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Contact ID Number</label>
                                        <input type="text" inputMode="numeric" className={inputClass('idNumber')} {...register('idNumber')} />
                                        {fieldError('idNumber')}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Ownership Percentage</label>
                                        <input type="number" min={0} max={100} step="0.01" className={inputClass('ownershipPercentage')} {...register('ownershipPercentage')} />
                                        {fieldError('ownershipPercentage')}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Average Monthly Turnover (ZAR)</label>
                                        <input type="number" min={0} step="0.01" className={inputClass('turnover')} {...register('turnover')} />
                                        {fieldError('turnover')}
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">Source of Income</label>
                                        <select className="form-select" {...register('incomeType')}>
                                            <option value="">Not specified</option>
                                            <option value="PO">Purchase order</option>
                                            <option value="TENDER">Tender</option>
                                            <option value="PRODUCTS_AND_SERVICES">Products and services</option>
                                            <option value="OTHER">Other</option>
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label fw-semibold mb-2">VAT Number</label>
                                        <input type="text" className="form-control" {...register('vatNumber')} />
                                    </div>
                                </>
                            )}

                            <div className="col-md-6">
                                <label className="form-label fw-semibold mb-2">Tax Number</label>
                                <input type="text" className="form-control" {...register('taxNumber')} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold mb-2">{isBusiness ? 'Primary Business Bank' : 'Bank'}</label>
                                <select className="form-select" {...register('bankName')}>
                                    <option value="">Select bank...</option>
                                    {southAfricanBanks.map(bank => (
                                        <option key={bank.name} value={bank.name}>{bank.name}</option>
                                    ))}
                                    {unlistedBank && <option value={bankName}>{bankName}</option>}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {errorMessage && (
                <div className="alert alert-danger d-flex align-items-center">
                    <i className="fa fa-exclamation-circle me-2"></i>
                    {errorMessage}
                </div>
            )}

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-3 mt-4 pt-3 border-top modal-action-bar">
                <button
                    type="button"
                    className="btn btn-outline-secondary px-5 py-2 rounded-pill"
                    {...(onCancel ? { onClick: onCancel } : { 'data-bs-dismiss': 'modal' })}
                    disabled={isPending}
                >
                    <i className="fa fa-times me-2"></i>
                    Cancel
                </button>
                <button
                    type="submit"
                    className="btn btn-primary px-5 py-2 rounded-pill shadow-sm fw-semibold"
                    disabled={isPending}
                    style={{ minWidth: '180px' }}
                >
                    {isPending ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {mode === 'create' ? 'Creating Lead...' : 'Saving...'}
                        </>
                    ) : (
                        <>
                            <i className="fa fa-check-circle me-2"></i>
                            {mode === 'create' ? 'Create Lead' : 'Save Changes'}
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default LeadForm;
